// src/pages/settings/SiteForm.tsx — formulaire de création/édition d'un site (Info / Détails / CVC / Comptage)

import { useEffect, useRef, useState } from "react";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import type { UserRole } from "../../auth/roles";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { uploadImage } from "../../api/uploadApi";
import { geocodeAddress } from "../../api/geoApi";
import { listPdl, listPce, type PdlRecord, type PceRecord } from "../../api/metersApi";
import { MAX_W_150DPI, PROP_LABELS, makeSiteId, to150dpi, type MeterEntry } from "./helpers";
import { ImageDropInput } from "./ImageDropInput";
import { MeterListInput } from "./MeterListInput";

type SiteFormTab = "info" | "details" | "cvc" | "comptage";

const SITE_FORM_TABS: { id: SiteFormTab; label: string }[] = [
  { id: "info",     label: "Info"     },
  { id: "details",  label: "Détails"  },
  { id: "cvc",      label: "CVC"      },
  { id: "comptage", label: "Comptage" },
];

// PROP_LABELS belonging to each tab
const PROPS_DETAILS = ["anneeConstruction","anneeRehabiliation","occupant","configuration","typologie","surface","horaire"];
const PROPS_CVC     = ["chauffage","rafraichissement"];

export function SiteForm({ initial, clients, sites, existingIds, onSave, onClose, userRole, setModal }:{
  initial?: SiteRecord; clients:ClientRecord[]; sites:SiteRecord[];
  existingIds:string[]; onSave:(s:SiteRecord)=>Promise<void>; onClose:()=>void;
  userRole?: UserRole;
  setModal: (m: ConfirmModalProps | null) => void;
}) {
  const cp0 = initial?.data.coverProps?.codePostal ?? "";
  const [clientId,    setClientId]    = useState(initial?.clientId ?? clients[0]?.id ?? "");
  const [name,        setName]        = useState(initial?.name ?? "");
  const [voie,        setVoie]        = useState(initial?.data.coverProps?.voie ?? "");
  const [codePostal,  setCodePostal]  = useState(cp0);
  const [commune,     setCommune]     = useState(initial?.data.coverProps?.commune ?? "");
  const [photo,       setPhoto]       = useState(initial?.data.coverProps?.photoUrl ?? "");
  const [lat,         setLat]         = useState(initial?.data.coverProps?.lat ?? "");
  const [lon,         setLon]         = useState(initial?.data.coverProps?.lon ?? "");
  // Adresse/coordonnées au montage — sert à détecter une modification avant de déclencher un nouveau géocodage
  const initialAddressRef = useRef({ voie: initial?.data.coverProps?.voie ?? "", codePostal: cp0, commune: initial?.data.coverProps?.commune ?? "" });
  const initialCoordsRef  = useRef({ lat: initial?.data.coverProps?.lat ?? "", lon: initial?.data.coverProps?.lon ?? "" });

  const [schema,      setSchema]      = useState(initial?.data.schemaCVC ?? "");
  const [desc,        setDesc]        = useState(initial?.data.description ?? "");
  const [props,       setProps]       = useState<Record<string,string>>(initial?.data.coverProps ?? {});
  const [pdls,    setPdls]    = useState<MeterEntry[]>(initial?.pdls ?? []);
  const [pces,    setPces]    = useState<MeterEntry[]>(initial?.pces ?? []);
  // Rattachement réel le plus récemment connu (rafraîchi par refreshMeters) — sert de référence à
  // computeMeterActions, plutôt que initial.pdls/pces (figé à l'ouverture de Paramètres, peut être
  // périmé) : ne remonte que les changements que l'utilisateur fait réellement dans cette session.
  const [baselinePdls, setBaselinePdls] = useState<MeterEntry[]>(initial?.pdls ?? []);
  const [baselinePces, setBaselinePces] = useState<MeterEntry[]>(initial?.pces ?? []);
  const [gtb,     setGtb]     = useState(initial?.data.coverProps?.gtb ?? "");
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState<string|null>(null);
  const [formTab, setFormTab] = useState<SiteFormTab>("info");

  // Rattachement site_id réel des PDL/PCE (source de vérité, cf. table pdl/pce) — permet de détecter
  // qu'un compteur saisi ici est déjà rattaché à un autre site avant d'écraser silencieusement ce lien.
  const [allPdls, setAllPdls] = useState<PdlRecord[]>([]);
  const [allPces, setAllPces] = useState<PceRecord[]>([]);

  // Recharge PDL/PCE depuis le serveur et resynchronise la liste affichée (pdls/pces) sur ce
  // rattachement réel — initial.pdls/pces (venant de la liste sites chargée à l'ouverture de
  // Paramètres) peut être périmé si un libellé a été modifié entretemps depuis l'onglet PDL/PCE.
  // Appelée au montage (fraîcheur à l'ouverture de la fiche) et en cas d'annulation de la
  // confirmation de rattachement (le formulaire redevient fidèle à l'état réel plutôt que de
  // garder des changements non confirmés).
  const refreshMeters = async () => {
    const [freshPdls, freshPces] = await Promise.all([listPdl(), listPce()]);
    setAllPdls(freshPdls);
    setAllPces(freshPces);
    if (initial) {
      const myPdls = freshPdls.filter(p => p.siteId === initial.id).map(p => ({ id: p.id, label: p.label }));
      const myPces = freshPces.filter(p => p.siteId === initial.id).map(p => ({ id: p.id, label: p.label }));
      setPdls(myPdls); setBaselinePdls(myPdls);
      setPces(myPces); setBaselinePces(myPces);
    }
  };
  useEffect(() => { refreshMeters().catch(()=>{}); }, []);

  const derivedId = initial?.id ?? (clientId && codePostal
    ? makeSiteId(clientId, codePostal, existingIds)
    : "…");

  // Compare la liste de compteurs affichée au chargement de la fiche à celle actuellement saisie —
  // ne remonte que les changements de rattachement (ajout/retrait/déplacement depuis un autre site),
  // jamais une simple modification de libellé sur un compteur déjà présent.
  const computeMeterActions = (): string[] => {
    const actions: string[] = [];
    const diff = (kind: "PDL"|"PCE", current: MeterEntry[], initialList: MeterEntry[], allRecords: {id:string; siteId:string}[]) => {
      const currentIds = new Set(current.map(m=>m.id.trim()).filter(Boolean));
      const initialIds = new Set(initialList.map(m=>m.id.trim()).filter(Boolean));
      for (const id of currentIds) {
        if (initialIds.has(id)) continue;
        const existing = allRecords.find(r=>r.id===id);
        if (existing?.siteId && existing.siteId !== (initial?.id ?? "")) {
          const fromName = sites.find(s=>s.id===existing.siteId)?.name ?? existing.siteId;
          actions.push(`${kind} ${id} : actuellement rattaché à « ${fromName} » → sera déplacé vers ce site`);
        } else {
          actions.push(`${kind} ${id} : sera rattaché à ce site`);
        }
      }
      for (const id of initialIds) {
        if (!currentIds.has(id)) actions.push(`${kind} ${id} : sera détaché de ce site`);
      }
    };
    diff("PDL", pdls, baselinePdls, allPdls);
    diff("PCE", pces, baselinePces, allPces);
    return actions;
  };

  const effectivePhoto  = photo;
  const effectiveSchema = schema;

  const handleSave = async () => {
    if(!name.trim()){ setErr("Le nom du site est obligatoire."); return; }
    if(!codePostal.trim()){ setErr("Le code postal est obligatoire."); return; }
    if(!clientId){ setErr("Sélectionnez un client."); return; }
    setSaving(true);
    setErr(null);
    try {
      const meterActions = computeMeterActions();
      if (meterActions.length > 0) {
        const proceed = await new Promise<boolean>(resolve => {
          setModal({
            title: "Confirmer les changements de compteurs",
            message: meterActions.join("\n"),
            confirmLabel: "Confirmer", cancelLabel: "Annuler",
            wide: true,
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        setModal(null);
        if (!proceed) { await refreshMeters().catch(()=>{}); setSaving(false); return; }
      }

      const a = initialAddressRef.current;
      const addressChanged  = voie.trim()!==a.voie || codePostal.trim()!==a.codePostal || commune.trim()!==a.commune;
      const hasStoredCoords = !!(initial?.data.coverProps?.lat && initial?.data.coverProps?.lon);

      // Coordonnées GPS modifiées manuellement dans le formulaire : priment sur le géocodage automatique.
      const c = initialCoordsRef.current;
      const coordsChanged = lat.trim()!==c.lat || lon.trim()!==c.lon;
      const manualLat = parseFloat(lat.trim()), manualLon = parseFloat(lon.trim());
      const hasManualCoords = coordsChanged && Number.isFinite(manualLat) && Number.isFinite(manualLon);

      // Géocode si l'adresse a changé, si les coordonnées ont été effacées manuellement, ou si le
      // site n'a encore jamais été géocodé (site créé avant cette fonctionnalité)
      const shouldGeocode = !hasManualCoords && (addressChanged || coordsChanged || !hasStoredCoords);

      // Coordonnées à écrire dans coverProps : undefined = ne pas toucher aux coordonnées existantes
      // (adresse inchangée), null = les effacer (géocodage abandonné/échoué)
      let latLon: { lat:number; lon:number } | null | undefined;
      if (hasManualCoords) {
        latLon = { lat: manualLat, lon: manualLon };
      } else if (shouldGeocode) {
        try {
          latLon = await geocodeAddress(commune.trim(), codePostal.trim());
        } catch {
          const proceed = await new Promise<boolean>(resolve => {
            setModal({
              title: "Adresse introuvable",
              message: `Le géocodage de l'adresse saisie a échoué (${[voie.trim(), codePostal.trim(), commune.trim()].filter(Boolean).join(", ")}). `
                + `Vérifiez le code postal et la commune (ou saisissez les coordonnées GPS manuellement), ou enregistrez quand même — le site n'apparaîtra pas sur la carte tant que l'adresse ne sera pas corrigée.`,
              confirmLabel: "Enregistrer quand même",
              cancelLabel: "Vérifier l'adresse",
              danger: true,
              wide: true,
              onConfirm: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          setModal(null);
          if (!proceed) { setSaving(false); return; }
          latLon = null;
        }
      }

      const adresse = [voie.trim(), codePostal.trim(), commune.trim()].filter(Boolean).join(", ");
      const [photoUrl, schemaUrl] = await Promise.all([
        effectivePhoto.startsWith("data:")  ? uploadImage(await to150dpi(effectivePhoto))  : effectivePhoto,
        effectiveSchema.startsWith("data:") ? uploadImage(await to150dpi(effectiveSchema)) : effectiveSchema,
      ]);
      // electricite et gaz retirés de coverProps — ils sont désormais dans les colonnes pdls/pces
      // lat/lon retirés puis réinjectés explicitement ci-dessous selon le résultat du géocodage/de la saisie manuelle
      // meteoFranceStationId retiré puis réinjecté ci-dessous seulement si les coordonnées n'ont pas changé
      const { electricite: _e, gaz: _g, lat: _oldLat, lon: _oldLon, meteoFranceStationId: oldStationId, ...cleanProps } = props;
      // undefined (adresse/coordonnées inchangées, pas de géocodage tenté) → conserve les coordonnées
      // actuellement affichées dans le formulaire ; null (géocodage abandonné/échoué) → les efface ;
      // objet → coordonnées manuelles ou fraîchement géocodées
      const fallbackLat = parseFloat(lat.trim()), fallbackLon = parseFloat(lon.trim());
      const finalLatLon = latLon === undefined
        ? (Number.isFinite(fallbackLat) && Number.isFinite(fallbackLon) ? { lat: fallbackLat, lon: fallbackLon } : null)
        : latLon;
      // Coordonnées effectivement modifiées (re-géocodage suite à un changement d'adresse, saisie
      // manuelle, ou effacement) → la station Météo-France rattachée n'est plus nécessairement la
      // plus proche : on la détache (l'utilisateur devra en choisir une nouvelle depuis la carte
      // des sites) plutôt que de laisser un DJU calculé sur une localisation obsolète.
      const newLatStr = finalLatLon ? String(finalLatLon.lat) : undefined;
      const newLonStr = finalLatLon ? String(finalLatLon.lon) : undefined;
      const coordsChangedForSave = newLatStr !== initial?.data.coverProps?.lat || newLonStr !== initial?.data.coverProps?.lon;
      const coverProps = { ...cleanProps,
        ...(coordsChangedForSave || !oldStationId ? {} : { meteoFranceStationId: oldStationId }),
        voie: voie.trim(), codePostal: codePostal.trim(), commune: commune.trim(),
        adresse,
        photoUrl,
        gtb,
        ...(finalLatLon ? { lat: String(finalLatLon.lat), lon: String(finalLatLon.lon) } : {}),
      };
      await onSave({
        id: initial?.id ?? derivedId,
        clientId, name: name.trim(),
        data: { name:name.trim(), description:desc, schemaCVC:schemaUrl, coverProps },
        pdls: pdls.filter(m => !!m.id.trim()),
        pces: pces.filter(m => !!m.id.trim()),
        createdAt:"", updatedAt:"",
      });
      onClose();
    } catch(e){ setErr((e as Error).message); setSaving(false); }
  };

  const setProp = (k:string,v:string) => setProps(p=>({...p,[k]:v}));

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      {/* Scrollable body */}
      <div style={{overflowY:"auto",padding:"20px 24px",flex:1,display:"flex",flexDirection:"column",minHeight:0}}>

        {/* ── Internal tabs ─────────────────────────────────────────────── */}
        <div style={{display:"flex",gap:4,marginBottom:16,background:"#f2f2f2",borderRadius:8,padding:4,flexShrink:0}}>
          {SITE_FORM_TABS.map(t=>(
            <button key={t.id} onClick={()=>setFormTab(t.id)}
              style={{flex:1,border:"none",cursor:"pointer",padding:"7px 0",borderRadius:6,
                fontFamily:"var(--ff-title)",fontSize:12,fontWeight:700,
                background:formTab===t.id?"#fff":"transparent",
                color:formTab===t.id?"var(--text-primary)":"var(--text-muted)",
                boxShadow:formTab===t.id?"0 1px 4px rgba(0,0,0,0.10)":"none",
                transition:"all 0.15s"}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab: Info */}
        {formTab==="info" && (
          <>
            {/* Client + Nom + Description + Voie (left) | Photo (right) */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12,alignItems:"stretch"}}>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {/* Client */}
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <label className="label" style={{marginBottom:0,flex:1}}>Client *</label>
                    <div className="id-tooltip-wrapper" style={{position:"relative",display:"none"}}>
                      <button title={`Identifiant : ${derivedId}`}
                        style={{width:24,height:24,borderRadius:"50%",border:"1.5px solid #ddd",background:"#f8f8f8",
                          cursor:"default",fontWeight:900,fontSize:11,color:"#888",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        ℹ
                      </button>
                      <div style={{position:"absolute",top:"50%",right:"calc(100% + 8px)",transform:"translateY(-50%)",
                        background:"#1a1a2e",color:"#fff",fontSize:11,padding:"6px 10px",borderRadius:6,
                        whiteSpace:"nowrap",pointerEvents:"none",opacity:0,transition:"opacity 0.15s",zIndex:10}}
                        className="id-tooltip-text">
                        id : <strong>{derivedId}</strong>
                      </div>
                    </div>
                    <button onClick={()=>navigator.clipboard?.writeText(derivedId)}
                      title="Copier l'identifiant"
                      style={{width:24,height:24,borderRadius:5,border:"1.5px solid #ddd",background:"#f8f8f8",
                        cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",
                        visibility:"hidden"}}>
                      id
                    </button>
                  </div>
                  <select value={clientId} onChange={e=>{setClientId(e.target.value);setErr(null);}} className="field">
                    {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {/* Nom */}
                <div>
                  <label className="label">Nom du site *</label>
                  <input value={name} onChange={e=>setName(e.target.value)} className="field"/>
                </div>
                {/* Description */}
                <div>
                  <label className="label">Description</label>
                  <input value={desc} onChange={e=>setDesc(e.target.value)} className="field"/>
                </div>
                {/* Voie */}
                <div>
                  <label className="label">Voie</label>
                  <input value={voie} onChange={e=>setVoie(e.target.value)} className="field" placeholder="ex : 1 rue de la Paix"/>
                </div>
              </div>
              {/* Photo */}
              <ImageDropInput label="Photo" value={photo} onChange={setPhoto} height="100%"
                cropTarget={{ w: MAX_W_150DPI, h: Math.round(MAX_W_150DPI * 3 / 4) }}
                canEditCropTarget={userRole === "admin" || userRole === "super-admin"}/>
            </div>

            {/* Code postal + Commune */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label className="label">Code postal *</label>
                <input value={codePostal} onChange={e=>{setCodePostal(e.target.value);setErr(null);}} className="field" placeholder="ex : 75001"/>
              </div>
              <div>
                <label className="label">Commune</label>
                <input value={commune} onChange={e=>setCommune(e.target.value)} className="field" placeholder="ex : Paris"/>
              </div>
            </div>

            {/* Coordonnées GPS — auto-calculées depuis l'adresse à l'enregistrement, modifiables manuellement */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:4}}>
              <div>
                <label className="label">Latitude</label>
                <input value={lat} onChange={e=>setLat(e.target.value)} className="field" placeholder="ex : 45.7640"/>
              </div>
              <div>
                <label className="label">Longitude</label>
                <input value={lon} onChange={e=>setLon(e.target.value)} className="field" placeholder="ex : 4.8357"/>
              </div>
            </div>
            <p style={{fontSize:11,color:"var(--text-faint)",marginTop:0,marginBottom:16}}>
              Calculées automatiquement depuis l'adresse à l'enregistrement si laissées telles quelles — renseignez-les pour forcer une position précise (ignore alors le géocodage de l'adresse).
            </p>
          </>
        )}

        {/* Tab: Détails */}
        {formTab==="details" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            {PROP_LABELS.filter(([k])=>PROPS_DETAILS.includes(k)).map(([k,lbl])=>(
              <div key={k}>
                <label className="label">{lbl}</label>
                <input value={props[k]??""} onChange={e=>setProp(k,e.target.value)} className="field"/>
              </div>
            ))}
          </div>
        )}

        {/* Tab: CVC */}
        {formTab==="cvc" && (
          <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12,flexShrink:0}}>
              {PROP_LABELS.filter(([k])=>PROPS_CVC.includes(k)).map(([k,lbl])=>(
                <div key={k}>
                  <label className="label">{lbl}</label>
                  <input value={props[k]??""} onChange={e=>setProp(k,e.target.value)} className="field"/>
                </div>
              ))}
            </div>
            <div className="mb-16" style={{flex:1,minHeight:0}}>
              <ImageDropInput label="Schéma CVC" value={schema} onChange={setSchema} height="100%"/>
            </div>
          </div>
        )}

        {/* Tab: Comptage */}
        {formTab==="comptage" && (
          <div>
            <MeterListInput label="PDL électricité ⚡" values={pdls} onChange={setPdls} idPlaceholder="ex : 12345678901234"/>
            <MeterListInput label="PCE gaz 🔥" values={pces} onChange={setPces} idPlaceholder="ex : GI123456"/>
            <div className="mb-16">
              <label className="label">GTB</label>
              <input value={gtb} onChange={e=>setGtb(e.target.value)} className="field" placeholder="ex : WIT"/>
            </div>
          </div>
        )}

      </div>{/* end scrollable body */}

      {/* Sticky footer */}
      <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
        {err && <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:13,color:"var(--red)"}}>⚠ {err}</div>}
        <div className="flex gap-10" style={{justifyContent:"flex-end"}}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-black btn-lg">
            {saving?"Enregistrement…":"Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
